import React from "react";

const SOURCE_STYLE = {
  table: { label: "Table", className: "bg-purple-100 text-purple-800" },
  takeaway: { label: "Takeaway", className: "bg-orange-100 text-orange-800" },
  online: { label: "Online", className: "bg-sky-100 text-sky-800" },
};

const MINUTE = 60 * 1000;

const mins = (ms) => Math.max(0, Math.floor(ms / MINUTE));
const clock = (date) =>
  new Date(date).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// How the ticket is doing against its expected cooking time
const timing = (ticket, now) => {
  if (ticket.status === "new") {
    const startsIn = ticket.estStartAt ? mins(new Date(ticket.estStartAt) - now) : 0;
    return {
      text: `Waiting ${mins(now - new Date(ticket.createdAt))}m`,
      sub: startsIn > 0 ? `start in ~${startsIn}m` : "start now",
      tone: "text-gray-600",
      bar: null,
    };
  }
  if (ticket.status === "preparing") {
    const elapsed = now - new Date(ticket.startedAt || ticket.createdAt);
    const progress = elapsed / (ticket.prepMin * MINUTE);
    const late = mins(elapsed - ticket.prepMin * MINUTE);
    return {
      text: `Cooking ${mins(elapsed)}m / ${Math.round(ticket.prepMin)}m`,
      sub: progress > 1 ? `${late}m late` : `ready ~${clock(ticket.estReadyAt || now)}`,
      tone: progress > 1 ? "text-red-700" : progress > 0.8 ? "text-amber-700" : "text-green-700",
      bar: { value: Math.min(progress, 1), color: progress > 1 ? "bg-red-500" : progress > 0.8 ? "bg-amber-500" : "bg-green-500" },
    };
  }
  if (ticket.status === "ready") {
    return { text: `Ready ${mins(now - new Date(ticket.readyAt || now))}m ago`, sub: null, tone: "text-green-700", bar: null };
  }
  return {
    text: ticket.status === "cancelled" ? "Cancelled" : `Served ${clock(ticket.servedAt || ticket.updatedAt)}`,
    sub: null,
    tone: "text-gray-500",
    bar: null,
  };
};

const TicketCard = ({ ticket, now, busy, onStatus, onToggleItem }) => {
  const source = SOURCE_STYLE[ticket.source] || SOURCE_STYLE.takeaway;
  const t = timing(ticket, now);
  const cooking = ticket.status === "new" || ticket.status === "preparing";
  const late = ticket.status === "preparing" && t.bar?.value >= 1;
  const handOver = ticket.source === "table" ? "Served" : "Picked up";

  const primary = {
    new: { to: "preparing", label: "Start cooking", className: "bg-blue-600 hover:bg-blue-700" },
    preparing: { to: "ready", label: "Mark ready", className: "bg-green-600 hover:bg-green-700" },
    ready: { to: "served", label: handOver, className: "bg-gray-800 hover:bg-gray-900" },
    served: { to: "ready", label: "Recall", className: "bg-amber-600 hover:bg-amber-700" },
    cancelled: { to: "new", label: "Restore", className: "bg-amber-600 hover:bg-amber-700" },
  }[ticket.status];
  const back = { preparing: "new", ready: "preparing" }[ticket.status];

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border-2 flex flex-col ${
        late ? "border-red-400" : ticket.status === "ready" ? "border-green-300" : "border-gray-200"
      } ${ticket.status === "cancelled" ? "opacity-60" : ""}`}
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        {/* Timer drops below the name when the column is narrow; the name is never cut off */}
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-extrabold text-gray-900 break-words">{ticket.label}</span>
              {ticket.isAddition && (
                <span className="text-[11px] font-bold uppercase bg-yellow-200 text-yellow-900 rounded px-1.5 py-0.5">
                  + More
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs text-gray-500">
              <span className={`font-semibold rounded px-1.5 py-0.5 ${source.className}`}>{source.label}</span>
              <span className="whitespace-nowrap">
                KOT #{ticket.ticketNo} · {clock(ticket.createdAt)}
              </span>
              {ticket.customerName && <span className="truncate max-w-full">{ticket.customerName}</span>}
            </div>
          </div>
          <div className={`ml-auto text-right shrink-0 ${t.tone}`}>
            <div className="text-sm font-bold whitespace-nowrap">{t.text}</div>
            {t.sub && <div className="text-xs whitespace-nowrap">{t.sub}</div>}
          </div>
        </div>
        {t.bar && (
          <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full ${t.bar.color}`} style={{ width: `${t.bar.value * 100}%` }} />
          </div>
        )}
      </div>

      {/* Dishes */}
      <ul className="p-2 flex-1 space-y-1">
        {ticket.items.map((item) => {
          const done = item.status === "done";
          const cancelled = item.status === "cancelled";
          return (
            <li key={item._id}>
              <button
                type="button"
                disabled={!cooking || cancelled || busy}
                onClick={() => onToggleItem(ticket, item, !done)}
                className={`w-full text-left flex items-start gap-3 rounded-lg px-2 py-2 ${
                  cooking && !cancelled ? "hover:bg-gray-50 active:bg-gray-100" : "cursor-default"
                }`}
              >
                {cooking && !cancelled && (
                  <span
                    className={`mt-0.5 w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center text-sm font-bold ${
                      done ? "bg-green-600 border-green-600 text-white" : "border-gray-300"
                    }`}
                    aria-hidden="true"
                  >
                    {done ? "✓" : ""}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-base font-semibold ${
                      cancelled ? "line-through text-gray-400" : done ? "text-gray-400" : "text-gray-900"
                    }`}
                  >
                    <span className="text-blue-700 font-extrabold">{item.quantity}×</span> {item.name}
                  </span>
                  {item.addOns?.length > 0 && (
                    <span className="block text-sm text-gray-600">+ {item.addOns.join(", ")}</span>
                  )}
                  {item.note && (
                    <span className="block text-sm font-semibold text-red-700 bg-red-50 rounded px-1.5 py-0.5 mt-0.5">
                      📝 {item.note}
                    </span>
                  )}
                  {cancelled && <span className="block text-xs font-bold text-red-600">REMOVED</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Actions */}
      {primary && (
        <div className="p-2 border-t border-gray-100 flex gap-2">
          {back && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onStatus(ticket, back)}
              className="px-3 py-3 rounded-lg border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
              title="Undo: move back one step"
            >
              ↩
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => onStatus(ticket, primary.to)}
            className={`flex-1 py-3 rounded-lg text-white text-base font-bold disabled:opacity-50 ${primary.className}`}
          >
            {busy ? "…" : primary.label}
          </button>
        </div>
      )}
    </div>
  );
};

export default TicketCard;
